import React, { useState, useEffect, useRef, useMemo } from 'react';
import { notify } from '../Notifications';
import { useTheme } from '../../context/ThemeContext';
import mermaid from 'mermaid';
import { CheckIcon, CodeIcon, CopyIcon, DownloadIcon, EyeIcon, SpinnerIcon } from '../icons';
import {
  globalDiagramCache,
  globalDiagramVisibility,
  renderingInProgress,
  createDigestKey,
} from './mermaidUtils';

const CodeBlock: React.FC<{
  children: React.ReactNode;
  noMargin?: boolean;
  language?: string;
}> = ({ children, noMargin = false, language }) => {
  const [codeCopySuccess, setCodeCopySuccess] = useState(false);
  const [codeDownloadSuccess, setCodeDownloadSuccess] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [renderingStatus, setRenderingStatus] = useState<'idle' | 'rendering' | 'complete'>('idle');
  const [forceUpdate, setForceUpdate] = useState(0);

  const diagramRef = useRef<HTMLDivElement>(null);

  const uniqueId = Math.random().toString(36).substring(2, 10);
  const diagramId = useRef<string>(`mermaid-diagram-${uniqueId}`).current;
  const { colors } = useTheme();
  const isMermaid = language === 'mermaid';

  const extractText = (node: React.ReactNode): string => {
    if (typeof node === 'string') 
      return node;
    if (typeof node === 'number') 
      return node.toString();
    if (Array.isArray(node)) 
      return node.map(extractText).join('');
    if (React.isValidElement(node) && node.props.children) {
      return extractText(node.props.children);
    }

    return '';
  };

  const codeText = extractText(children);

  const codeDigest = useMemo(() => {
    if (!codeText)
      return '';

    return createDigestKey(codeText);
  }, [codeText]);

  const [showDiagram, setShowDiagram] = useState(() => {
    return globalDiagramVisibility[codeDigest] || false;
  });

  // Pre-render diagrams when component mounts
  useEffect(() => {
    if (!isMermaid || !codeText || !codeDigest) 
      return;

    if (globalDiagramCache[codeDigest] || renderingInProgress[codeDigest]) {
      return;
    }

    renderingInProgress[codeDigest] = true;
    setRenderingStatus('rendering');

    const diagramId = codeDigest;

    mermaid.render(diagramId, codeText)
      .then(({ svg }) => {
        let modifiedSvg = svg;
        if (!svg.includes('style="display: block; margin: auto;')) {
          modifiedSvg = svg.replace('<svg ', '<svg style="display: block; margin: auto;" ');
        }

        globalDiagramCache[codeDigest] = modifiedSvg;
        renderingInProgress[codeDigest] = false;
        setRenderingStatus('complete');
        setForceUpdate(prev => prev + 1);
      })
      .catch(error => {
        console.error('Mermaid rendering error:', error);
        setRenderError(`Error rendering diagram: ${error.message || 'Invalid syntax'}`);
        renderingInProgress[codeDigest] = false;
        setRenderingStatus('idle');
      });
  }, [isMermaid, codeText, codeDigest]);

  // Sync visibility state to global
  useEffect(() => {
    if (isMermaid && codeDigest) {
      globalDiagramVisibility[codeDigest] = showDiagram;
    }
  }, [isMermaid, codeDigest, showDiagram]);

  // Load initial state from global visibility
  useEffect(() => {
    if (isMermaid && codeDigest && globalDiagramVisibility[codeDigest] !== undefined) {
      setShowDiagram(globalDiagramVisibility[codeDigest]);
    }
  }, [isMermaid, codeDigest]);

  const toggleDiagramView = () => {
    const newState = !showDiagram;
    setShowDiagram(newState);
    if (codeDigest) {
      globalDiagramVisibility[codeDigest] = newState;
    }
  };

  const handleCodeCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeText);
      setCodeCopySuccess(true);
      setTimeout(() => setCodeCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy code: ', err);
    }
  };

  const getRenderedSvgString = (id: string): string | null => {
    try {
      let renderedSvg = null;

      if (diagramRef.current) {
        renderedSvg = diagramRef.current.querySelector('svg');
      }

      if (!renderedSvg) {
        const container = document.getElementById(id);
        if (container) {
          renderedSvg = container.querySelector('svg');
        }
      }

      if (!renderedSvg) {
        renderedSvg = document.querySelector('.mermaid-diagram-container svg') ||
                      document.querySelector('.mermaid svg');
      }

      if (renderedSvg) {
        const serializer = new XMLSerializer();
        return serializer.serializeToString(renderedSvg);
      }

      return null;
    } catch (error) {
      console.error('Error accessing rendered SVG:', error);

      return null;
    }
  };

  const downloadFile = (content: string | Blob, filename: string, type: string) => {
    try {
      let url: string;

      if (typeof content === 'string') {
        if (content.startsWith('data:') || content.startsWith('blob:')) {
          url = content;
        } else {
          const blob = new Blob([content], { type });
          url = URL.createObjectURL(blob);
        }
      } else {
        url = URL.createObjectURL(content);
      }

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);

      setTimeout(() => {
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          if (url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
          
          setCodeDownloadSuccess(true);
          setTimeout(() => setCodeDownloadSuccess(false), 2000);
        }, 100);
      }, 0);
    } catch (error) {
      console.error('Download error:', error);
      notify('Failed to download file. Please try again.', 'error');
    }
  };

  const handleCodeDownload = () => {
    try {
      if (isMermaid && showDiagram) {
        const svgString = getRenderedSvgString(diagramId) || globalDiagramCache[codeDigest];
        if (!svgString) {
          throw new Error('Could not find SVG content to download');
        }

        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const filename = `diagram-${timestamp}.svg`;
        downloadFile(svgString, filename, 'image/svg+xml');
      } else {
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const extension = language === 'mermaid' ? 'mmd' : 'txt';
        downloadFile(codeText, `code-${timestamp}.${extension}`, 'text/plain');
      }
    } catch (err) {
      console.error('Failed to download:', err);
      notify('Download failed. Please try again.', 'error');
    }
  };

  return (
    <div className={`cv-code-block${noMargin ? ' cv-code-block--embedded' : ''}`}>
      {/* Header row with language label and buttons */}
      <div style={{
        backgroundColor: 'transparent',
        padding: '8px 12px 4px 12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {language && (
          <div style={{
            fontSize: '12px',
            color: colors.textMuted,
            padding: '3px 8px',
            borderRadius: '4px',
            backgroundColor: colors.surface,
            fontFamily: 'monospace',
            fontWeight: '500',
            textTransform: 'lowercase',
            letterSpacing: '0.3px'
          }}>
            {language}
          </div>
        )}

        <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
          {isMermaid && (
            <button
              type="button"
              className="cv-tool-btn"
              onClick={toggleDiagramView}
              title={showDiagram ? "Show code" : "Render diagram"}
            >
              {showDiagram ? <CodeIcon size={13} /> : <EyeIcon size={13} />}
              <span>{showDiagram ? 'Code' : 'Display'}</span>
            </button>
          )}

          <button
            type="button"
            className={`cv-tool-btn${codeCopySuccess ? ' is-done' : ''}`}
            onClick={handleCodeCopy}
            title="Copy code"
          >
            {codeCopySuccess ? <CheckIcon size={13} /> : <CopyIcon size={13} />}
            <span>{codeCopySuccess ? 'Copied' : 'Copy'}</span>
          </button>
          <button
            type="button"
            className={`cv-tool-btn${codeDownloadSuccess ? ' is-done' : ''}`}
            onClick={handleCodeDownload}
            title="Download code as .txt file"
          >
            {codeDownloadSuccess ? <CheckIcon size={13} /> : <DownloadIcon size={13} />}
            <span>{codeDownloadSuccess ? 'Downloaded' : 'Download'}</span>
          </button>
        </div>
      </div>

      {/* Conditional rendering based on mermaid diagram toggle */}
      {isMermaid && showDiagram ? (
        <div
          ref={diagramRef}
          id={diagramId}
          data-testid="mermaid-diagram-container"
          className="mermaid-diagram-container"
          style={{
            padding: '12px',
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            backgroundColor: colors.surface,
            minHeight: '100px',
            transition: 'height 0.3s ease-in-out'
          }}
          key={`diagram-${codeDigest}-${forceUpdate}`}
        >
          {renderError ? (
            <div style={{ color: 'red', padding: '10px' }}>{renderError}</div>
          ) : globalDiagramCache[codeDigest] ? (
            <div
              key={`svg-${codeDigest}`}
              dangerouslySetInnerHTML={{ __html: globalDiagramCache[codeDigest] }}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
              className="mermaid-svg-container"
            />
          ) : (
            <div style={{
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <span style={{ color: colors.primary, display: 'flex' }}><SpinnerIcon size={16} /></span>
              <span style={{ color: colors.textMuted }}>Rendering diagram…</span>
            </div>
          )}
        </div>
      ) : (
        <pre style={{
          backgroundColor: 'transparent',
          padding: '0 12px 12px 12px',
          borderRadius: '0',
          overflow: 'auto',
          fontSize: '14px',
          fontFamily: 'monospace',
          margin: '0',
          color: colors.textPrimary
        }}>
          {children}
        </pre>
      )}
    </div>
  );
};

export default CodeBlock;